import { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MarkInactiveModal,
  MiModalConfirm,
  PrioritySchedulerBoard,
  PrioritySchedulerHeaderActionType,
  RemoveInactiveModal,
} from "@miplatform/components";
import { ATTEMPT_STATUSES } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  Colors,
  IAttemptListItemResponseForWorkOrder,
  IUserListItemResponse,
} from "@miplatform/types";
import { mdiCancel } from "@mdi/js";
import { Box } from "@mui/material";
import { AssignFromFollowUpModal } from "./modals/AssignFromFollowUpModal/AssignFromFollowUpModal";
import { useCalendarContext } from "../../../providers";
import { FOLLOW_UP_TRAY_FILTERS } from "../../Config/Calendars/ConfigCalendarSetList.constants";
import { CalendarToolBar } from "../CalendarToolBar/CalendarToolBar";
import {
  applyCalendarFilters,
  useCalendarFilterGroup,
} from "../CalendarToolBar/useCalendarFilterGroup";
import { buildDateColumns, buildUserColumns } from "../buildColumns.util";
import { NHCWorkOrderCard } from "./NHCWorkOrderCard";
import { prioritySchedulerHeaderActions } from "./SchedulerView.constants";

export function SchedulerView({
  assigneeModeUser,
}: Readonly<{
  assigneeModeUser?: IUserListItemResponse;
}>) {
  const { theme, classes } = useStyles();
  const [followUpModalUser, setFollowUpModalUser] =
    useState<IUserListItemResponse | null>(null);
  const [markInactiveModalUser, setMarkInactiveModalUser] =
    useState<IUserListItemResponse | null>(null);
  const [removeInactiveModalUser, setRemoveInactiveModalUser] =
    useState<IUserListItemResponse | null>(null);
  const [clearScheduleModalUser, setClearScheduleModalUser] =
    useState<IUserListItemResponse | null>(null);
  const { navigate } = useRouter();

  const {
    showFilters,
    toggleFilters,
    filters,
    appliedFilters,
    builderOptions,
    communityOptions,
    statusOptions,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    handleFilterChange,
  } = useCalendarFilterGroup();

  const {
    assignableUsers,
    calendarSet,
    date,
    events,
    isCalendarLoading,
    setZoom,
    zoom,
    users,
    tenantHolidays,
    handleSelectUser,
    selectedUser,
    trayBuckets,
    loadUsers,
    clearWorkOrderSchedule,
    setSelectedAttempt,
    getTrayDataForCalendar,
  } = useCalendarContext();

  useEffect(() => {
    getTrayDataForCalendar();
  }, [calendarSet?.config?.id]);

  const activeTrays = useMemo(() => {
    if (!trayBuckets?.length) return [];

    const bucketsById = trayBuckets.reduce((acc, bucket) => {
      acc[bucket.id] = bucket;
      return acc;
    }, {} as Record<string, (typeof trayBuckets)[0]>);

    return trayBuckets.reduce((acc, tray) => {
      if (tray.isActive) {
        const bucket = bucketsById[tray.id];
        if (bucket) {
          acc.push(bucket);
        }
      }
      return acc;
    }, [] as (typeof trayBuckets)[0][]);
  }, [trayBuckets]);

  const selectedAssignee = assigneeModeUser || selectedUser;

  const filteredEvents = useMemo(
    () =>
      applyCalendarFilters(events, appliedFilters).filter(
        ({ attemptStatus }) => attemptStatus !== ATTEMPT_STATUSES.HOLD
      ),
    [events, appliedFilters]
  );

  const followUpAttempts = useMemo(
    (): IAttemptListItemResponseForWorkOrder[] =>
      trayBuckets
        .filter(({ filter }) => filter === FOLLOW_UP_TRAY_FILTERS)
        .flatMap(
          ({ data }) => (data ?? []) as IAttemptListItemResponseForWorkOrder[]
        ),
    [trayBuckets]
  );

  const columns = useMemo(() => {
    const currentUsers = users.length > 0 ? users : assignableUsers;
    if (!currentUsers) return [];

    if (selectedAssignee) {
      return buildDateColumns({
        date,
        events: filteredEvents,
        user: currentUsers.find((user) => user.id === selectedAssignee.id),
      });
    }
    return buildUserColumns({
      users: currentUsers,
      date,
      events: filteredEvents,
    });
  }, [assignableUsers, users, filteredEvents, date, selectedAssignee]);

  const handleHeaderActionClick = useCallback(
    (
      actionType: PrioritySchedulerHeaderActionType,
      user?: IUserListItemResponse
    ) => {
      if (!user) return;
      const selectedUser = users.find((u) => u.id === user.id) || user;

      switch (actionType) {
        case PrioritySchedulerHeaderActionType.ViewMore:
          handleSelectUser(selectedUser);
          break;
        case PrioritySchedulerHeaderActionType.AddFromFollowUp:
          setFollowUpModalUser(selectedUser);
          break;
        case PrioritySchedulerHeaderActionType.MarkInactive:
          setMarkInactiveModalUser(selectedUser);
          break;
        case PrioritySchedulerHeaderActionType.RemoveInactive:
          setRemoveInactiveModalUser(selectedUser);
          break;
        case PrioritySchedulerHeaderActionType.ClearSchedule:
          setClearScheduleModalUser(selectedUser);
          break;
        case PrioritySchedulerHeaderActionType.ViewProfile:
          navigate(`/profile/${selectedUser?.id}`);
          break;
        default:
          break;
      }
    },
    [users, navigate, handleSelectUser]
  );

  useEffect(() => {
    handleSelectUser(null);
  }, []);

  return (
    <Box className={classes.container}>
      <Box className={classes.topRow}>
        <CalendarToolBar
          zoom={zoom}
          setZoom={setZoom}
          showCalendarFilters={showFilters}
          toggleCalendarFilters={toggleFilters}
          filters={filters}
          appliedFilters={appliedFilters}
          builderOptions={builderOptions}
          communityOptions={communityOptions}
          statusOptions={statusOptions}
          hasPendingFilters={hasPendingFilters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          handleFilterChange={handleFilterChange}
          title={
            selectedAssignee
              ? `${selectedAssignee.displayName}'s Schedule`
              : "Schedule"
          }
        />
      </Box>
      <Box overflow="auto">
        <PrioritySchedulerBoard
          columns={columns}
          date={date}
          zoom={zoom}
          columnWidth={180}
          hourHeight={60}
          tenantHolidays={tenantHolidays}
          calendarSet={calendarSet?.config ?? null}
          headerActions={prioritySchedulerHeaderActions}
          onHeaderActionClick={handleHeaderActionClick}
          onCardClick={setSelectedAttempt}
          EventComponent={NHCWorkOrderCard}
          trays={selectedUser ? [] : activeTrays}
        />
      </Box>
      {clearScheduleModalUser && (
        <MiModalConfirm
          title={`Clear ${clearScheduleModalUser?.displayName}'s Schedule?`}
          titleIcon={{
            path: mdiCancel,
            color: theme.palette.primary.main,
          }}
          disableClick={isCalendarLoading}
          textPositive="Clear Schedule"
          open={!!clearScheduleModalUser}
          handleNegative={() => setClearScheduleModalUser(null)}
          handlePositive={() => {
            clearWorkOrderSchedule(clearScheduleModalUser);
            setClearScheduleModalUser(null);
          }}
          description={`You are about clear ${clearScheduleModalUser?.displayName}'s schedule. Would you like to proceed?`}
          buttonPositiveColor={Colors.Primary}
        />
      )}
      {followUpModalUser && (
        <AssignFromFollowUpModal
          open={!!followUpModalUser}
          onClose={() => setFollowUpModalUser(null)}
          user={followUpModalUser}
          assignedAttempts={columns
            .flatMap((column) => column.events || [])
            .filter((event) => event.assignedUserId === followUpModalUser.id)}
          followUpAttempts={followUpAttempts}
          calendarSet={calendarSet?.config ?? null}
        />
      )}
      {removeInactiveModalUser && (
        <RemoveInactiveModal
          {...({
            open: !!removeInactiveModalUser,
            onClose: () => setRemoveInactiveModalUser(null),
            user: removeInactiveModalUser,
            onRefresh: loadUsers,
          } as React.ComponentProps<typeof RemoveInactiveModal>)}
        />
      )}
      {markInactiveModalUser && (
        <MarkInactiveModal
          {...({
            open: !!markInactiveModalUser,
            onClose: () => setMarkInactiveModalUser(null),
            user: markInactiveModalUser,
            onRefresh: loadUsers,
          } as React.ComponentProps<typeof MarkInactiveModal>)}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  topRow: {
    backgroundColor: theme.palette.background.default,
  },
}));
